<script setup lang="ts">
/**
 * Root Application Component — Donor Portal
 *
 * Main app entry with global components
 */
import { onMounted } from 'vue';
import { RouterView } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { AppToast, provideTranslation, useIdleTimeout, useSessionSync } from '@tms-phoenix/ui-vue';
import { useUIStore, useAuthStore } from '@/stores';
import { useLocale } from '@/shared/composables/useLocale';

const uiStore = useUIStore();
const authStore = useAuthStore();

useIdleTimeout(
  async () => {
    await authStore.logout();
    // logout() already calls router.push('/login') internally
  },
  { active: () => authStore.isAuthenticated },
);

// Sign this tab out when another tab ends or replaces the stored session (e.g.
// a different person signs in after this one was signed out), so a stale tab
// cannot keep acting as — or write back the tokens of — the previous user.
useSessionSync({
  storageKey: 'pinia-auth',
  getUserId: () => authStore.user?.id ?? null,
});
const { t } = useI18n();
const { locale } = useLocale();

// Provide the i18n translation function to all shared @tms-phoenix/ui-vue
// components so they auto-localize without explicit label props.
provideTranslation(
  (key: string, defaultValue?: string) => {
    const result = t(key);
    return result === key ? (defaultValue ?? key) : result;
  },
  locale,
);

// Apply theme on mount
onMounted(() => {
  uiStore.applyTheme();
});
</script>

<template>
  <div
    id="app"
    class="h-screen overflow-hidden bg-linear-[135deg,#E1EEFC,#F9F0FF] font-sans"
  >
    <!-- Router view with transitions -->
    <RouterView v-slot="{ Component, route }">
      <Transition
        :name="route.meta.transition as string || 'fade'"
        mode="out-in"
      >
        <component
          :is="Component"
          :key="route.path"
        />
      </Transition>
    </RouterView>

    <!-- Global Components -->
    <AppToast />
  </div>
</template>

<style>
/* Prevent page scroll */
html, body {
  overflow: hidden;
  height: 100vh;
  margin: 0;
  padding: 0;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
